import { FormType, ReportJSON, randomUUID } from '@aloft/toolkit-shared';

import JSZip from 'jszip';

import { generateTotalXmlReport } from './generateTotalXmlReport.ts';

// Toolkit Vendor ID
const VENDOR_ID = '979C2842-36E0-4483-B116-7C46CFBC1041';

type GenerateTCDXReportArgs = {
  reportJSON: ReportJSON;
  orderID: string;
  formType: FormType;
  pdfs: { blob: Blob; name: string }[];
  includeAnnotations: boolean;
};

export const generateTdcxReport = async ({
  reportJSON,
  orderID,
  formType,
  pdfs,
  includeAnnotations,
}: GenerateTCDXReportArgs): Promise<string> => {
  const doc = document.implementation.createDocument('', '', null);
  const pi = doc.createProcessingInstruction('xml', 'version="1.0" encoding="utf-8"');
  doc.insertBefore(pi, doc.firstChild);

  const data = createDataNode(doc, orderID);
  doc.appendChild(data);

  const properties = doc.createElement('Properties');
  data.appendChild(properties);

  // Create the Total XML Report data and Base64 encode it
  const reportData = generateTotalXmlReport(reportJSON, formType, includeAnnotations);

  const jszip = new JSZip();
  jszip.file('Report.xml', reportData);
  const zippedFile = await jszip.generateAsync({ type: 'base64' });

  const reports = doc.createElement('Reports');
  const report = doc.createElement('Report');
  report.setAttribute('Description', 'Appraisify Toolkit Export');
  report.textContent = zippedFile;
  reports.appendChild(report);
  data.appendChild(reports);

  // Attachments, goes into workfile
  const attachments = doc.createElement('Attachments');
  data.appendChild(attachments);
  for (const pdf of pdfs) {
    // Create an attachment node
    const base64 = (await blobToBase64(pdf.blob)) as string;
    if (typeof base64 !== 'string') continue;
    const attachment = createAttachmentNode(doc, pdf.name, pdf.name, randomUUID(), base64);
    attachments.appendChild(attachment);
  }

  const serializer = new XMLSerializer();
  const reportString = serializer.serializeToString(doc);
  return reportString;
};

const createDataNode = (doc: XMLDocument, orderID: string): HTMLElement => {
  const data = doc.createElement('Data');
  data.setAttribute('Version', '1.0');

  data.setAttribute('VendorId', VENDOR_ID);
  data.setAttribute('VendorTransactionID', orderID);

  return data;
};

function createAttachmentNode(
  doc: XMLDocument,
  fileName: string,
  description: string,
  id: string,
  base64Data: string,
) {
  const attachment = doc.createElement('Attachment');
  attachment.setAttribute('Id', id);
  attachment.setAttribute('Description', description);
  attachment.setAttribute('FileName', fileName);
  attachment.textContent = base64Data;

  return attachment;
}

function blobToBase64(blob: Blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result?.toString()?.replace(/data:[A-z]+\/[A-z]+;base64,/, '');
      return resolve(result);
    };
    reader.readAsDataURL(blob);
  });
}
